import { Link } from "@tanstack/react-router";
import { LogOut, Menu, Settings, Sun, Type } from "lucide-react";
import { useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { useCapabilities } from "../session/capabilities";
import { CommandPalette } from "./CommandPalette";
import { FreshnessChip } from "./FreshnessChip";
import { NotificationCentre } from "./NotificationCentre";
import { ScopeBreadcrumb } from "./ScopeBreadcrumb";

function formatRole(role: string | null): string {
  if (!role) return "User";
  return role.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function TopBar({ onMenuClick }: { onMenuClick?: () => void }) {
  const caps = useCapabilities();
  const { logout } = useAuth();
  const tenant = caps.accessible_tenants[0]?.name ?? "SolarKey";
  const roleLabel = formatRole(caps.role);

  return (
    <header style={topbarStyle} className="sk-topbar">
      <button
        type="button"
        onClick={onMenuClick}
        aria-label="Menu"
        className="sk-menu-btn"
        style={menuBtnStyle}
      >
        <Menu aria-hidden size={16} />
      </button>

      {/* Scope breadcrumb — §2.1 element 2 (Disha's D-1) */}
      <ScopeBreadcrumb />

      {/* Search — single element, opens CommandPalette overlay on click */}
      <CommandPalette />

      <span style={{ flex: 1 }} />

      {/* Right cluster: freshness, notifications, avatar */}
      <FreshnessChip />
      <NotificationCentre />
      <AvatarMenu
        initials={tenant.slice(0, 2).toUpperCase()}
        roleLabel={roleLabel}
        tenant={tenant}
        onLogout={logout}
      />
    </header>
  );
}

function AvatarMenu({ initials, roleLabel, tenant, onLogout }: {
  initials: string; roleLabel: string; tenant: string; onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button
        type="button"
        style={avatarStyle}
        aria-label="User menu"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((v) => !v)}
      >
        {initials}
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-label="Close menu"
            style={{ position: "fixed", inset: 0, background: "transparent", border: 0, zIndex: 59 }}
            onClick={() => setOpen(false)}
          />
          <div style={menuStyle}>
            <div style={identityStyle}>
              <div style={identityNameStyle}>{initials}</div>
              <div style={identityMetaStyle}>
                {roleLabel} &middot; {tenant}
              </div>
            </div>
            <hr style={{ margin: "4px 0", border: 0, borderTop: "1px solid var(--sk-color-border-1)" }} />
            <Link
              to="/preferences"
              onClick={() => setOpen(false)}
              style={menuItemStyle}
            >
              <Settings aria-hidden size={14} />
              Preferences
            </Link>
            <button type="button" style={menuItemStyle} onClick={() => {
              const root = document.documentElement;
              const next = root.dataset.theme === "dark" ? "light" : "dark";
              root.dataset.theme = next;
              setOpen(false);
            }}>
              <Sun aria-hidden size={14} />
              Toggle theme
            </button>
            <button type="button" style={menuItemStyle} onClick={() => {
              const root = document.documentElement;
              const next = root.dataset.density === "compact" ? "comfortable" : "compact";
              root.dataset.density = next;
              setOpen(false);
            }}>
              <Type aria-hidden size={14} />
              {document.documentElement.dataset.density === "compact" ? "Comfortable" : "Compact"}
            </button>
            <hr style={{ margin: "4px 0", border: 0, borderTop: "1px solid var(--sk-color-border-1)" }} />
            <button type="button" style={{ ...menuItemStyle, color: "var(--sk-color-health-fault)" }} onClick={() => {
              setOpen(false);
              onLogout();
            }}>
              <LogOut aria-hidden size={14} />
              Sign out
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

const topbarStyle: React.CSSProperties = {
  height: 48,
  background: "var(--sk-color-surface-0)",
  borderBottom: "1px solid var(--sk-color-border-1)",
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "0 16px",
  flexShrink: 0,
};

const menuBtnStyle: React.CSSProperties = {
  display: "none",
  alignItems: "center",
  justifyContent: "center",
  background: "none",
  border: "1px solid var(--sk-color-border-1)",
  borderRadius: 6,
  padding: 4,
  cursor: "pointer",
  color: "var(--sk-color-ink-3)",
};

const avatarStyle: React.CSSProperties = {
  width: 26,
  height: 26,
  borderRadius: "50%",
  background: "var(--sk-color-surface-2)",
  border: "1px solid var(--sk-color-border-2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 10,
  fontWeight: 600,
  color: "var(--sk-color-ink-2)",
  cursor: "pointer",
  flexShrink: 0,
  padding: 0,
};

const menuStyle: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 6px)",
  right: 0,
  zIndex: 60,
  minWidth: 180,
  padding: "4px 0",
  background: "var(--sk-color-surface-0)",
  border: "1px solid var(--sk-color-border-1)",
  borderRadius: 8,
  boxShadow: "0 4px 16px rgba(10,10,15,.12)",
};

const menuItemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "8px 12px",
  border: 0,
  background: "none",
  color: "var(--sk-color-ink-2)",
  fontSize: 12,
  cursor: "pointer",
  textDecoration: "none",
};

const identityStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  padding: "8px 12px 6px",
};

const identityNameStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--sk-color-ink-1)",
};

const identityMetaStyle: React.CSSProperties = {
  fontSize: 11,
  color: "var(--sk-color-ink-3)",
};
